import React from "react";

import { OnboardingStep } from "graphql/types";
import { isPosthogFeatureFlagEnabled, FeatureFlag } from "lib/analytics/featureFlags";
import { useAppRouter } from "lib/useAppRouter";
import { Route } from "lib/routes";
import links from "lib/links";

import { LaunchpadStep } from "../launchpadStore";
import { TaskTile } from "../TaskTile";
import { LaunchpadTask } from "../analytics";
import { LaunchpadTaskSetProps } from "./LaunchpadTaskSetProps";

export const DefaultLaunchpadTaskSet = ({
  fullSize,
  initialIngestCompleted,
  onSkip,
  onComplete,
  onGoToStepAndShow,
  onTaskClick,
  localProgress,
  onboardingProgress,
}: LaunchpadTaskSetProps) => {
  const {
    goToReconciliations,
    goToUsers,
    goToIntegrations,
    goToChecklist,
    goToTransactionsShortcut,
    goToPath,
  } = useAppRouter();

  return (
    <>
      <TaskTile
        heading="Set your bookkeeping plan"
        subheading="Unlock the full power of Puzzle by adding a bookkeeper."
        stepStatus={
          localProgress?.setBookkeepingPlan ??
          onboardingProgress?.companyOnboardingProgress?.setBookkeepingPlan
        }
        onClick={() =>
          onGoToStepAndShow(
            LaunchpadStep.BookKeepingPlanSelection,
            localProgress?.setBookkeepingPlan ??
              onboardingProgress?.companyOnboardingProgress?.setBookkeepingPlan
          )
        }
        onComplete={() =>
          onComplete(
            OnboardingStep.SetBookkeepingPlan,
            localProgress?.setBookkeepingPlan ??
              onboardingProgress?.companyOnboardingProgress?.setBookkeepingPlan
          )
        }
        analyticsTask={LaunchpadTask.SetBookkeepingPlan}
        completeOnClick={false}
        completeTooltip="Please let us know what your bookkeeping plan is."
        fullSize={fullSize}
      />
      {/* This step TBD, needs BE support if/when we add it
          <TaskTile
            heading="Set your tax plan"
            subheading="Puzzle can help you be tax ready."
            completed={false}
            stepStatus={onboardingProgress?.setBookkeepingPlan}
            onClick={() => console.log("click")}
            analyticsTask={LaunchpadTask.SetTaxPlan}
            fullSize={fullSize}
          />*/}
      <TaskTile
        heading="Verify your bank account balances"
        subheading="Ensure the foundation of your accounting is accurate."
        stepStatus={
          localProgress?.verifyBankBalances ??
          onboardingProgress?.companyOnboardingProgress?.verifyBankBalances
        }
        onClick={() =>
          onTaskClick(
            goToReconciliations,
            localProgress?.verifyBankBalances ??
              onboardingProgress?.companyOnboardingProgress?.verifyBankBalances
          )
        }
        onComplete={() =>
          onComplete(
            OnboardingStep.VerifyBankBalances,
            localProgress?.verifyBankBalances ??
              onboardingProgress?.companyOnboardingProgress?.verifyBankBalances
          )
        }
        analyticsTask={LaunchpadTask.VerifyBankBalances}
        fullSize={fullSize}
        disabled={!initialIngestCompleted}
      />
      {isPosthogFeatureFlagEnabled(FeatureFlag.TransactionsEducationBanner) && (
        <TaskTile
          heading="Review & categorize your transactions"
          subheading="Learn how to navigate and categorize your transactions faster than you ever have."
          stepStatus={
            localProgress?.reviewTransactions ??
            onboardingProgress?.userOnboardingProgress?.reviewTransactions
          }
          onClick={() =>
            onTaskClick(
              goToTransactionsShortcut,
              localProgress?.reviewTransactions ??
                onboardingProgress?.userOnboardingProgress?.reviewTransactions
            )
          }
          onComplete={() =>
            onComplete(
              OnboardingStep.ReviewTransactions,
              localProgress?.reviewTransactions ??
                onboardingProgress?.userOnboardingProgress?.reviewTransactions
            )
          }
          analyticsTask={LaunchpadTask.RreviewCategorizeTransactions}
          fullSize={fullSize}
          disabled={!initialIngestCompleted}
        />
      )}
      <TaskTile
        heading="Start your historical accounting data transfer"
        subheading="Book a call with one of our experts."
        stepStatus={
          localProgress?.startHistoricalTransferProcess ??
          onboardingProgress?.companyOnboardingProgress?.startHistoricalTransferProcess
        }
        onClick={() =>
          onGoToStepAndShow(
            LaunchpadStep.StartHistoricalTransferProcess,
            localProgress?.startHistoricalTransferProcess ??
              onboardingProgress?.companyOnboardingProgress?.startHistoricalTransferProcess
          )
        }
        onComplete={() =>
          onComplete(
            OnboardingStep.StartHistoricalTransferProcess,
            localProgress?.startHistoricalTransferProcess ??
              onboardingProgress?.companyOnboardingProgress?.startHistoricalTransferProcess
          )
        }
        analyticsTask={LaunchpadTask.StartHistoricalTransferProcess}
        completeOnClick={false}
        fullSize={fullSize}
      />
      <TaskTile
        heading="Review variances with Spotlight"
        subheading="Quickly detect variances in your cash activity report."
        stepStatus={
          localProgress?.reviewSpotlightVariances ??
          onboardingProgress?.userOnboardingProgress?.reviewSpotlightVariances
        }
        onClick={() =>
          onTaskClick(
            () => goToPath("/", { query: { tour: "spotlight" } }),
            localProgress?.reviewSpotlightVariances ??
              onboardingProgress?.userOnboardingProgress?.reviewSpotlightVariances
          )
        }
        onComplete={() =>
          onComplete(
            OnboardingStep.ReviewSpotlightVariances,
            localProgress?.reviewSpotlightVariances ??
              onboardingProgress?.userOnboardingProgress?.reviewSpotlightVariances
          )
        }
        onSkip={() => onSkip(OnboardingStep.ReviewSpotlightVariances)}
        analyticsTask={LaunchpadTask.ReviewVariancesWithSpotlight}
        fullSize={fullSize}
        disabled={!initialIngestCompleted}
      />
      <TaskTile
        heading="Invite your team"
        subheading="Invite your team to help you categorize, close your books, and increase visibility into your financial situation."
        stepStatus={
          localProgress?.inviteTeam ?? onboardingProgress?.companyOnboardingProgress?.inviteTeam
        }
        onClick={() =>
          onTaskClick(
            goToUsers,
            localProgress?.inviteTeam ?? onboardingProgress?.companyOnboardingProgress?.inviteTeam
          )
        }
        onComplete={() =>
          onComplete(
            OnboardingStep.InviteTeam,
            onboardingProgress?.companyOnboardingProgress?.inviteTeam
          )
        }
        onSkip={() => onSkip(OnboardingStep.InviteTeam)}
        analyticsTask={LaunchpadTask.InviteYourTeam}
        fullSize={fullSize}
      />
      <TaskTile
        heading="Review your monthly close checklist"
        subheading="Close your books with ease."
        stepStatus={
          localProgress?.reviewMonthEndChecklist ??
          onboardingProgress?.userOnboardingProgress?.reviewMonthEndChecklist
        }
        onComplete={() =>
          onComplete(
            OnboardingStep.ReviewMonthEndChecklist,
            localProgress?.reviewMonthEndChecklist ??
              onboardingProgress?.userOnboardingProgress?.reviewMonthEndChecklist
          )
        }
        onClick={() =>
          onTaskClick(
            goToChecklist,
            localProgress?.reviewMonthEndChecklist ??
              onboardingProgress?.userOnboardingProgress?.reviewMonthEndChecklist
          )
        }
        analyticsTask={LaunchpadTask.ReviewMonthlyCloseChecklist}
        fullSize={fullSize}
        disabled={!initialIngestCompleted}
      />
      <TaskTile
        heading="Connect all of your financial accounts"
        subheading="Increase the accuracy of your data by connecting all of your accounts."
        stepStatus={
          localProgress?.connectFinancialAccounts ??
          onboardingProgress?.userOnboardingProgress?.connectFinancialAccounts
        }
        onClick={() =>
          onTaskClick(
            goToIntegrations,
            localProgress?.connectFinancialAccounts ??
              onboardingProgress?.userOnboardingProgress?.connectFinancialAccounts
          )
        }
        onComplete={() =>
          onComplete(
            OnboardingStep.ConnectFinancialAccounts,
            localProgress?.connectFinancialAccounts ??
              onboardingProgress?.userOnboardingProgress?.connectFinancialAccounts
          )
        }
        onSkip={() => onSkip(OnboardingStep.ConnectFinancialAccounts)}
        analyticsTask={LaunchpadTask.ConnectFinancialAccounts}
        fullSize={fullSize}
      />
      <TaskTile
        heading="Get Puzzle certified"
        subheading="Become a certified Puzzle pro."
        stepStatus={
          localProgress?.getPuzzleCertified ??
          onboardingProgress?.userOnboardingProgress?.getPuzzleCertified
        }
        onClick={() =>
          onTaskClick(
            () => window.open(links.puzzleCertification, "_blank")?.focus(),
            localProgress?.getPuzzleCertified ??
              onboardingProgress?.userOnboardingProgress?.getPuzzleCertified
          )
        }
        onComplete={() =>
          onComplete(
            OnboardingStep.GetPuzzleCertified,
            localProgress?.getPuzzleCertified ??
              onboardingProgress?.userOnboardingProgress?.getPuzzleCertified
          )
        }
        onSkip={() => onSkip(OnboardingStep.GetPuzzleCertified)}
        analyticsTask={LaunchpadTask.GetPuzzleCertified}
        fullSize={fullSize}
      />
      <TaskTile
        heading="Configure your advanced accounting settings"
        subheading="Configure subledgers, automations and other advanced settings."
        stepStatus={
          localProgress?.configureAccountSettings ??
          onboardingProgress?.companyOnboardingProgress?.configureAccountSettings
        }
        onClick={() =>
          onTaskClick(
            () => goToPath(Route.automationSettings),
            localProgress?.configureAccountSettings ??
              onboardingProgress?.companyOnboardingProgress?.configureAccountSettings
          )
        }
        onComplete={() =>
          onComplete(
            OnboardingStep.ConfigureAccountSettings,
            localProgress?.configureAccountSettings ??
              onboardingProgress?.companyOnboardingProgress?.configureAccountSettings
          )
        }
        onSkip={() => onSkip(OnboardingStep.ConfigureAccountSettings)}
        analyticsTask={LaunchpadTask.ConfigureYourAccountingSettings}
        fullSize={fullSize}
      />
    </>
  );
};
